import React, {
    useEffect,
    useState
} from 'react';
import { useQuery } from '@tanstack/react-query';

import { getBasketData } from "../router";

import * as CONSTANTS from "../constants";
import * as QUERIES from "../query-functions";
import * as UTILS from "../utils";
import TickIcon from "../svg/tick";
import InfoCircle from "../svg/info-circle";
import SeatLockIcons from '../svg/seatlock-icons';

export default function SeatButton(props) {

    const [status, setStatus] = useState(null);
    const [seatBand, setSeatBand] = useState(null);
    const [seatColor, setSeatColor] = useState(null);
    const [ticketTypeId, setTicketTypeId] = useState(null);
    const [disabled, setDisabled] = useState(false);
    const [isSelected, setIsSelected] = useState(false);
    const [seatInfo, setSeatInfo] = useState(null);
    const [lockType, setLockType] = useState(null);

    const { data: basketData, isFetching: basketDataFetching } = getBasketData();

    const {
        data: priceListData,
    } = useQuery({
        queryKey: ["priceListData", CONSTANTS.INSTANCE_ID],
        queryFn: () => QUERIES.getInstancePriceList(CONSTANTS.INSTANCE_ID),
        staleTime: 86400000,
        refetchOnWindowFocus: false,
    });
    
    useEffect(() => {
        
        // Get seat status
        if (!props.statusData || !props.statusData.status[props.seat.id] || props.seatTicketIsPending) return;
        
        // Map statuses to seats
        const statusMap = {
            A: "is-available",
            B: "is-in-basket",
            U: "is-unavailable",
            W: "is-wheelchair-locked",
            X: "is-locked"
        };
        const seat_status = statusMap[props.statusData.status[props.seat.id].status] || "";
        setStatus(seat_status);

        if (seat_status == "is-in-basket") {
            setIsSelected(true);
        } else {
            setIsSelected(false);
        }
                
        // Check for lock types
        if (seat_status == "is-wheelchair-locked") {
            setLockType("Wheelchair Seat");
        }

        if (props.statusData.status[props.seat.id].lockType) {
            const lock_type = props.statusData.status[props.seat.id].lockType.toLowerCase();

            // Sometimes client's don't use the lock type correctly so this logic is here to ensure that Companion 
            // and Wheelchair lock types get the correct icons even if the status isn't correct
            if (lock_type.startsWith("e -") || lock_type.includes("companion") || lock_type.includes("escort")) {
                setLockType(props.statusData.status[props.seat.id].lockType);
            } else if (lock_type.includes("wheelchair") || seat_status == "is-wheelchair-locked") {
                setLockType("Wheelchair Seat");
            } else if (lock_type.includes("caption")) {
                setLockType("Closed Captions");
            } else if (lock_type.includes("audio")) {
                setLockType("Audio Description");
            } else if (lock_type.includes("bsl")) {
                setLockType("British Sign Language");
            } else if (lock_type.includes("relaxed")) {
                setLockType("Relaxed Performance");
            } else if (lock_type.includes("friends")) {
                setLockType("Friends allocation");
            } else {
                setLockType(props.statusData.status[props.seat.id].lockType);
            }
        }

        // Find out if there is extra info
        if (props.statusData.info[props.seat.id]) {
            setSeatInfo(props.statusData.info[props.seat.id]);
        }

        // Match the seat band id to the instance pricelist to find the band colour
        let seat_band = priceListData?.find((price) => {
            return price.priceBand.id == props.statusData.status[props.seat.id].priceBandId
                    && price.ticketType.name == CONSTANTS.DEFAULT_TICKET_TYPE
        });
        
        if (!seat_band) return;

        // Set the ticket type ID to be used in the add/remove queries
        setTicketTypeId(seat_band.ticketType?.id);
        setSeatBand(seat_band);

        if (seat_status == "is-available" || seat_status == "is-in-basket" || lockType == "Friends allocation" ) {
            // Allows enabling of filter buttons in select-seats when data is available
            props.setEnabledFilters(prev => {
                const updated = [...prev];
                if (props.area && !updated.includes(props.area.id)) updated.push(props.area.id);
                
                const bandAmountStr = seat_band.amount.toString();
                if (!updated.includes(bandAmountStr)) updated.push(bandAmountStr);

                return updated;
              });
        }
        
    }, [props.statusData, priceListData, lockType]);

    useEffect(() => {
        if (!props.groupedPrices || !CONSTANTS.COLORS || !seatBand) return;

        const matched_price = props.groupedPrices.find((price) => price.amount == seatBand.amount);
        setSeatColor(matched_price?.color);
    }), [props.groupedPrices, seatBand];
    
    useEffect(() => {

        // Toggle button filtering by price and area
        // The disabled prop also gives the button un available styles. 
        // If you don't want unavailable styles set the disabled attribute directly in the render
        if  (   
                (props.selectedBands && seatBand && !props.selectedBands.includes(seatBand.amount)) ||
                (props.selectedArea && props.selectedArea.id != props.area?.id)
            ) {
            setDisabled(true);
        } else {
            setDisabled(false);
        }
        
    }, [props.selectedBands, seatBand, props.selectedArea, props.selectedSeatCount]);

     
    
    const handleOnClick = () => {
        if (isSelected) {
            let seat_basket_id = null;
    
            if (basketData) {
                // Get basket id from basket data
                const basket_item = UTILS.getBasketTicketById(basketData, props.seat.id);
                seat_basket_id = basket_item?.id;
            }
            
            if (!seat_basket_id) return;
            
            props.removeTicket(seat_basket_id);
    
            // TO DO: Set this to "is-info" if that is the original state
            setStatus("is-available");
            setIsSelected(false);
        }
        if (!isSelected) {
            props.addTicket({ticketTypeId, seatId: props.seat.id});
            setIsSelected(true);
        }
    }
    


    if (!props.seat || !status) return null;

    if ((status === "is-unavailable" || status === "is-sold") && !isSelected) {        
        return (
            <p className={`seating-plan__seat ${status}`}
                style={{top: props.seat.y+"px", left: props.seat.x+"px"}}>
                <span className='sr-text'>Seat {props.seat.name} is unavailable</span>
            </p>
        )
    }
    return (
        <button type="button" 
                className={`seating-plan__seat ${status} ${disabled ? 'disabled' : ''} ${props.seat.name} ${props.isWidow ? 'is-widow' : ''}`}
                style={{top: props.seat.y+"px", left: props.seat.x+"px", backgroundColor: seatColor ? seatColor : "", color: seatColor ? seatColor : ""}}
                aria-selected={isSelected}
                disabled={
                    disabled ||
                    (isSelected && (basketDataFetching || props.seatTicketIsPending)) ||
                    (!isSelected && (props.maxTickets && props.selectedSeatCount >= props.maxTickets))
                }
                onClick={() => {handleOnClick()} }>
                { isSelected ? 
                    <>
                        <span className='sr-text'>Deselect seat {props.seat.name}</span>
                        <TickIcon /> 
                    </>
                    : 
                    <span className='sr-text'>Select seat {props.seat.name}</span> 
                }

                {(lockType || seatInfo) && (
                    <span className='seating-plan__more-info'>
                        {lockType ? (
                            <>
                                <strong className='sr-text'>{lockType}</strong>
                                <SeatLockIcons icon={lockType} />
                            </>
                        ) : seatInfo ? (
                            <>
                                <strong className='sr-text'>{seatInfo?.priceBandId?.replaceAll("+", " ")}</strong>
                                <InfoCircle />
                            </>
                        ) : null}
                    </span>
                )}
        </button>
    );
}